import { Button, Chip, FormControl, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";

import { OnBookingCompletedCallback } from "../../apiHooks";
import { ProductQuery } from "../../apiHooks/graphql";
import { AvailabilityForm } from "../../containers";
import { useNextTranslation, useProductPromotionConfig } from "../../hooks";
import { BookingConsumerTripInput } from "../../types/ProductFullLoaded";
import { AvailabilityContact } from "./AvailabilityContact";
import { AvailabilityDatePicker } from "./AvailabilityDatePicker";
import { AvailabilityExternalWidget } from "./AvailabilityExternalWidget";
import { AvailabilityHotelModalHeader } from "./AvailabilityHotelModalHeader";
import { AvailabilitySelectWrapper } from "./AvailabilityLayoutComponents";
import { AvailabilityPass } from "./AvailabilityPass";

const StyledHr = styled("hr")({
    color: "inherit",
    border: "0",
    margin: "1rem 0",
    borderTop: "1px solid #E0E0E0",
});

const StyledChip = styled(Chip)(({ theme }) => ({
    fontSize: "14px",
    color: theme.palette.common.white,
}));

const CheckAvailabilityButton = styled(Button)({
    height: "56px",
    color: "#fff",
});

export type AvailabilityProps = {
    product: ProductQuery["product"];
    onBookingCompleted?: OnBookingCompletedCallback;
    bookingReference?: string;
    onAvailabilityChange?: (availability: string | null) => void;
    consumerTrip?: BookingConsumerTripInput;
};
export const Availability = ({
    product,
    onBookingCompleted,
    bookingReference,
    onAvailabilityChange,
    consumerTrip,
}: AvailabilityProps) => {
    const [availabilityListItemId, setAvailabilityListItemId] = useState<string | null>(null);
    const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
    const [t] = useNextTranslation("availability");

    const promotionConfig = useProductPromotionConfig({
        promotionType: product.promotionType ?? undefined,
        isFeatured: product.isFeatured ?? undefined,
    });

    const isAvailabilityFormVisible =
        availabilityListItemId && showAvailabilityForm && product.availabilityType !== "EXTERNAL_WIDGET";

    const shouldAutoFillQuestions = product.type !== "HOTEL";

    if (product.isOffSale) {
        // Please do not remove this console.log since it is needed for debug purposes.
        console.log({ productOffSaleReasonList: product.offSaleReasonList });
        return (
            <AvailabilitySelectWrapper>
                <ProductOffsaleText>{t("message.offSale")}</ProductOffsaleText>
            </AvailabilitySelectWrapper>
        );
    }
    let availabilityHeader: React.ReactElement;
    if (product.availabilityType === "EXTERNAL_WIDGET" && product.externalWidgetData) {
        availabilityHeader = <AvailabilityExternalWidget widget={product.externalWidgetData} />;
    } else if (product.availabilityType === "PASS") {
        availabilityHeader = (
            <AvailabilityPass
                productId={product.id}
                onAvailabilityUpdated={(availabilityListItemId) => {
                    if (availabilityListItemId) {
                        setAvailabilityListItemId(availabilityListItemId);
                        setShowAvailabilityForm(true);
                        if (onAvailabilityChange) {
                            onAvailabilityChange(availabilityListItemId);
                        }
                    }
                }}
            />
        );
    } else if (product.availabilityType === "CONTACT") {
        availabilityHeader = <AvailabilityContact product={product} />;
    } else if (product.availabilityType === "DATE" || product.availabilityType === "DATE_TIME") {
        if (product.type === "HOTEL") {
            availabilityHeader = (
                <AvailabilityHotelModalHeader
                    product={product}
                    onChange={(availabilityListItemId, isDefault) => {
                        setAvailabilityListItemId(availabilityListItemId);
                        if (!isDefault) {
                            setShowAvailabilityForm(true);
                        }
                        if (onAvailabilityChange) {
                            onAvailabilityChange(availabilityListItemId);
                        }
                    }}
                />
            );
        } else {
            availabilityHeader = (
                <FormControl fullWidth>
                    <AvailabilityDatePicker
                        productId={product.id}
                        timeZone={product.startPlace?.timeZone}
                        availabilityListItemId={availabilityListItemId}
                        onChange={(availabilityListItemId, isDefault) => {
                            setAvailabilityListItemId(availabilityListItemId);
                            if (!isDefault) {
                                setShowAvailabilityForm(true);
                            }
                            if (onAvailabilityChange) {
                                onAvailabilityChange(availabilityListItemId);
                            }
                        }}
                    />
                </FormControl>
            );
        }
    } else {
        throw new Error(
            `Unknown availability type: ${product.availabilityType}. Product data: ${JSON.stringify(product)}`
        );
    }

    return (
        <AvailabilitySelectWrapper>
            {promotionConfig && product.isFeatured && product.promotionType && (
                <div>
                    <StyledChip style={{ backgroundColor: promotionConfig.color }} label={promotionConfig.label} />
                    <StyledHr />
                </div>
            )}

            {availabilityHeader}
            {!showAvailabilityForm &&
                (product.availabilityType === "DATE" || product.availabilityType === "DATE_TIME") && (
                    <FormControl fullWidth margin="normal">
                        <CheckAvailabilityButton
                            variant="contained"
                            color="primary"
                            disabled={!availabilityListItemId}
                            onClick={() => setShowAvailabilityForm(true)}
                        >
                            {t("action.checkAvailability")}
                        </CheckAvailabilityButton>
                    </FormControl>
                )}

            {isAvailabilityFormVisible && (
                <>
                    <br />
                    <AvailabilityForm
                        key={availabilityListItemId}
                        availabilityId={availabilityListItemId}
                        onBookingCompleted={onBookingCompleted}
                        bookingReference={bookingReference}
                        shouldAutoFillQuestions={shouldAutoFillQuestions}
                        consumerTrip={consumerTrip}
                    />
                </>
            )}

            {product.cancellationPolicy.hasFreeCancellation && (
                <FreeCancellationWrapper>
                    <FreeCancellationHeader>{t("message.freeCancellation")}</FreeCancellationHeader>
                    {product.cancellationPolicy.penaltyList.nodes[0] && (
                        <FreeCancellationText>
                            {product.cancellationPolicy.penaltyList.nodes[0].formattedText}
                        </FreeCancellationText>
                    )}
                </FreeCancellationWrapper>
            )}
        </AvailabilitySelectWrapper>
    );
};

const FreeCancellationHeader = styled(Typography)(({ theme }) => ({
    "&": {
        fontSize: 16,
        fontWeight: "bold",
        color: theme.palette.success.main,
    },
}));

const FreeCancellationWrapper = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const FreeCancellationText = styled(Typography)(({ theme }) => ({
    "&": {
        color: theme.palette.text.secondary,
        fontSize: 14,
    },
}));

const ProductOffsaleText = styled(Typography)(({ theme }) => ({
    "&": {
        color: theme.palette.text.secondary,
        fontSize: 16,
    },
}));
